import { orgQueryKeys } from '@/config/queryKeys';
import { ORGS_QUERY_GC_TIME, ORGS_QUERY_STALE_TIME } from '@/constants';
import { fetchOrganizations } from '@/pages/Organizations/orgUtils';
import { InstrumentRunDataRow, Org } from '@/types';
import type { SelectionOption } from '@/types/autocomplete';
import { useQuery } from '@tanstack/react-query';
import type { ICellRendererParams } from 'ag-grid-enterprise';
import React, { FC, useMemo } from 'react';

import { SelectionCellRenderer } from './shared/SelectionCellRenderer';

// Format display value outside the component to avoid recreating on every render
const formatDisplayValue = (val: string | number | null | undefined): string => {
  if (val === null || val === undefined || val === '') return '';
  return String(val);
};

/**
 * Custom cell renderer for Organization Name selection with Dialog interface
 * Uses the shared SelectionCellRenderer for consistent UX
 * Supports bulk updating empty organization names via checkbox
 *
 * On initial load, displays all organizations available to the user.
 * Search functionality filters the initial organization list.
 */
interface OrganizationNameCellRendererProps extends ICellRendererParams<InstrumentRunDataRow> {
  allRowData?: InstrumentRunDataRow[];
  onBulkApply?: (rows: InstrumentRunDataRow[]) => void;
}

export const OrganizationNameCellRenderer: FC<OrganizationNameCellRendererProps> = ({
  allRowData,
  onBulkApply,
  ...props
}) => {
  // Use TanStack Query to get cached organizations data
  const { data: organizations = [] } = useQuery<Org[]>({
    queryKey: orgQueryKeys.list(),
    queryFn: fetchOrganizations,
    staleTime: ORGS_QUERY_STALE_TIME,
    gcTime: ORGS_QUERY_GC_TIME,
  });

  // Convert organizations to autocomplete options - these are the initial options
  // that will be shown when the dialog opens and filtered during search
  const organizationOptions = useMemo<SelectionOption[]>(() => {
    return organizations.map(org => ({
      id: String(org.id),
      key: String(org.id),
      label: org.name,
      value: String(org.id),
    }));
  }, [organizations]);

  return (
    <SelectionCellRenderer
      {...props}
      allRowData={allRowData}
      applyToEmptyKey="org_apply_to_all_empty"
      dialogTitleKey="org_select_dialog_title"
      emptyHintKey="org_select_empty_hint"
      fieldName="org_name"
      formatDisplayValue={formatDisplayValue}
      onBulkApply={onBulkApply}
      options={organizationOptions}
      placeholderKey="org_select_placeholder"
      saveValue="label"
    />
  );
};

OrganizationNameCellRenderer.displayName = 'OrganizationNameCellRenderer';

export default OrganizationNameCellRenderer;
